/**
 * MobileLogin — Phone-number-first login screen with Consumer / Vendor
 * toggle. Because SMS OTP requires Twilio (not yet enabled), the
 * "Get OTP" button routes to the existing JWT /login flow with the
 * phone pre-filled — so users can still complete authentication using
 * the SocialPulse email/password path today, and we'll swap in true
 * OTP once the Twilio integration lands.
 *
 * A second "Go to Intro / Home" pair is placed below the register
 * link so users always have a clear escape hatch.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const FP_PURPLE = "#653091";
const FP_GREEN = "#9CCA45";

const TABS = [
  { key: "consumer", label: "Login as Consumer" },
  { key: "vendor",   label: "Login as Vendor" },
];

export default function MobileLogin() {
  const nav = useNavigate();
  const [role, setRole] = useState("consumer");
  const [phone, setPhone] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!/^\d{7,15}$/.test(phone)) {
      toast.error("Enter a valid phone number");
      return;
    }
    // Twilio SMS OTP not wired yet — hand off to the existing JWT auth
    // page with the phone pre-filled + role stored for post-login use.
    localStorage.setItem("fp_intended_role", role);
    nav(`/login?next=%2Fm&hint=phone&phone=${encodeURIComponent(phone)}`);
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden flex flex-col"
      style={{
        background:
          "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><g fill=%22none%22 stroke=%22%23c9d47a%22 stroke-width=%221%22><circle cx=%2240%22 cy=%2260%22 r=%2220%22/><circle cx=%22160%22 cy=%22140%22 r=%2216%22/><rect x=%22110%22 y=%2230%22 width=%2224%22 height=%2224%22 rx=%224%22/><path d=%22M20 150 q20 -20 40 0%22/></g></svg>') repeat, linear-gradient(180deg,#F6FAE7 0%,#FFFDF3 100%)",
      }}
      data-testid="mobile-login"
    >
      {/* Brand mark */}
      <div className="flex justify-center pt-14 pb-4">
        <div className="w-20 h-20 rounded-full bg-white shadow-md flex items-center justify-center">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: FP_PURPLE, color: FP_GREEN, fontFamily: "serif", fontSize: 22, fontWeight: 700 }}
          >
            FP
          </div>
        </div>
      </div>

      <div className="px-6 pb-8">
        {/* Role toggle */}
        <div className="flex bg-white rounded-full p-1 mb-6 border border-gray-200">
          {TABS.map((t) => {
            const active = t.key === role;
            return (
              <button
                key={t.key}
                onClick={() => setRole(t.key)}
                data-testid={`login-role-${t.key}`}
                className="flex-1 py-3 rounded-full text-sm font-semibold transition-colors"
                style={{
                  background: active ? FP_GREEN : "transparent",
                  color: active ? "#fff" : "#1c1c1c",
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-3xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-center mb-6">Login</h2>
          <form onSubmit={submit} className="space-y-5">
            <div className="flex gap-2">
              <div className="flex items-center gap-1 border border-gray-300 rounded-lg px-3 bg-white">
                <span aria-hidden>🇺🇸</span>
                <span className="text-sm">+1</span>
              </div>
              <input
                data-testid="login-phone-input"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="Phone Number *"
                className="flex-1 border border-gray-300 rounded-lg px-3 py-3 outline-none focus:border-purple-500"
              />
            </div>
            <button
              type="submit"
              data-testid="login-get-otp"
              className="w-full py-3.5 rounded-full text-white text-base font-semibold shadow"
              style={{ background: FP_PURPLE }}
            >
              Get OTP
            </button>
          </form>
        </div>

        <p className="text-center mt-6 text-sm">
          Don't have an account?{" "}
          <Link to="/register?next=%2Fm" data-testid="login-register-link" className="font-bold" style={{ color: FP_GREEN }}>
            REGISTER
          </Link>
        </p>

        <div className="mt-6 flex flex-col items-center gap-2">
          <div className="flex gap-3">
            <button
              onClick={() => nav("/m/welcome/1")}
              data-testid="login-goto-intro"
              className="px-5 py-2.5 rounded-full text-white font-semibold"
              style={{ background: FP_PURPLE }}
            >
              Go to Intro
            </button>
            <button
              onClick={() => nav("/m")}
              data-testid="login-goto-home"
              className="px-5 py-2.5 rounded-full text-white font-semibold"
              style={{ background: FP_GREEN }}
            >
              Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
